import * as CSS from "csstype";
import { Flex, VStack, Box, Text, Divider, HStack } from "@chakra-ui/react";
import { MessageFragment, MessageThreadFragment } from "gql/graphql";
import React, { memo, useMemo } from "react";
import { MESSAGE_STATE, MessageSubType } from "modules/utils/Constants";
import useMessageThreadTranslation from "modules/hooks/use-message-thread-translation";
import colors from "app/colors";
import { TranslateIcon } from "modules/icons";
import { isMessageEmpty } from "modules/utils/Functions";
import MessageFiles from "modules/messaging/messages/message-files";
import MessageText from "modules/messaging/messages/message-text";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  messageState: MESSAGE_STATE;
}

const HappycoWorkOrderMessage: React.FC<IProps> = ({
  messageThread,
  message,
  messageState,
}) => {
  const { isViewingTranslatedMessage } = useMessageThreadTranslation(
    message,
    messageThread,
  );

  const messageFontStyle = useMemo((): CSS.Property.FontStyle => {
    if (message.subType === MessageSubType.OUT_OF_OFFICE) {
      return "italic";
    }
    return "normal";
  }, [message.subType]);

  if (isMessageEmpty(message)) {
    return (
      <VStack w="full" alignItems="flex-start" py={2}>
        <Text fontStyle="italic" fontSize={12} color={colors.grayMain}>
          No description in Happyco
        </Text>
        <Divider borderColor={colors.grayPrimary} />
      </VStack>
    );
  }

  return (
    <VStack w="full" spacing={1} alignItems="flex-start" pb={2}>
      <Flex w="full" align="center" pb={2}>
        <Text
          fontFamily="Outfit"
          textTransform="uppercase"
          color={colors.blackSecondary}
          fontSize={12}
          fontWeight={600}
          whiteSpace="nowrap"
        >
          Description & Files
        </Text>
        <Divider ml={2} borderColor={colors.blackSecondary} />
      </Flex>
      <HStack w="full" alignItems="flex-start">
        {isViewingTranslatedMessage && (
          <TranslateIcon boxSize={3} fill={colors.grayMain} mt={1} />
        )}
        <Box
          color={colors.grayMain}
          fontWeight={500}
          fontSize={14}
          fontStyle={messageFontStyle}
          sx={{ margin: "0 !important" }}
        >
          <MessageText
            messageThread={messageThread}
            message={message}
            displayEdited={false}
          />
        </Box>
      </HStack>
      <MessageFiles
        messageThread={messageThread}
        message={message}
        messageState={messageState}
        fileThumbSize={[125, 125]}
      />
      <Divider py={2} borderColor={colors.grayPrimary} />
    </VStack>
  );
};

export default memo(HappycoWorkOrderMessage);
